import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  Select,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Link,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { AppContext } from './AppContext';

const PayerPolicyResearch = () => {
  const { accessToken } = useContext(AppContext);
  
  const [configs, setConfigs] = useState([]);
  const [topLevels, setTopLevels] = useState([]);
  
  const [topic, setTopic] = useState('IONM');
  const [selectedTopLevel, setSelectedTopLevel] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [availableStates, setAvailableStates] = useState([]);
  const [currentEntity, setCurrentEntity] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Fetch configs on mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const response = await callApi('/api/v1/admin/payer_configs', accessToken);
        const data = await response.json();
        if (data.status === 'success') {
          setConfigs(data.configs);
          
          // Extract unique top levels
          const uniqueTopLevels = [...new Set(data.configs.map(c => c.top_level))];
          setTopLevels(uniqueTopLevels);
        }
      } catch (err) {
        console.error("Failed to fetch payer configs", err);
      }
    };
    
    if (accessToken) {
      fetchConfigs();
    }
  }, [accessToken]);

  // Handle Top Level change
  useEffect(() => {
    if (!selectedTopLevel) {
      setAvailableStates([]);
      setSelectedState('');
      setCurrentEntity(null);
      return;
    }

    const relevantConfigs = configs.filter(c => c.top_level === selectedTopLevel);
    
    // If it's a national payer (state = ALL)
    if (relevantConfigs.length === 1 && relevantConfigs[0].state === 'ALL') {
      setAvailableStates(['ALL']);
      setSelectedState('ALL');
      setCurrentEntity(relevantConfigs[0]);
    } else {
      // It's a state-based payer like BCBS
      const states = relevantConfigs.map(c => c.state).sort();
      setAvailableStates(states);
      setSelectedState('');
      setCurrentEntity(null);
    }
  }, [selectedTopLevel, configs]);

  // Handle State change
  useEffect(() => {
    if (selectedTopLevel && selectedState) {
      const config = configs.find(c => c.top_level === selectedTopLevel && c.state === selectedState);
      setCurrentEntity(config || null);
    }
  }, [selectedState, selectedTopLevel, configs]);

  const runResearch = async () => {
    if (!selectedTopLevel || !selectedState) {
      setError("Payer and State are required.");
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await callApi('/api/v1/admin/research_payer_policy', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic,
          top_level: selectedTopLevel,
          state: selectedState
        }),
      });
      
      const data = await response.json();

      if (data.status === 'error') {
        setError(data.message || 'An error occurred during research.');
      } else {
        setResults(data.results);
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to the server.');
    } finally {
      setLoading(false);
    }
  };

  const runFullRefresh = async () => {
    if (!window.confirm("Are you sure you want to trigger a full background refresh? This will delete existing cache and run live web searches for ALL payers. This may take several minutes.")) {
      return;
    }

    try {
      const response = await callApi('/api/v1/admin/refresh_all_payer_policies', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic }),
      });
      
      const data = await response.json();

      if (data.status === 'error') {
        setError(data.message || 'An error occurred triggering the refresh.');
      } else {
        alert(data.message || "Background refresh started successfully.");
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to the server.');
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      
      <Box maxW="7xl" mx="auto" py={8} px={4}>
        <VStack spacing={6} align="stretch">
          
          <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
            <HStack justify="space-between" mb={2}>
              <Heading size="lg">Commercial Payer Policy Research</Heading>
              <Button size="sm" colorScheme="red" variant="outline" onClick={runFullRefresh}>
                Trigger Full Background Refresh
              </Button>
            </HStack>
            <Text color="gray.600" mb={6}>
              Test the web-search-enabled LLM extraction for commercial medical necessity policies.
            </Text>

            <HStack spacing={4} align="flex-end">
              <FormControl isRequired maxW="200px">
                <FormLabel>Topic</FormLabel>
                <Input 
                  value={topic} 
                  onChange={(e) => setTopic(e.target.value)} 
                  placeholder="e.g., IONM"
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Top Level Payer</FormLabel>
                <Select 
                  placeholder="Select Payer" 
                  value={selectedTopLevel}
                  onChange={(e) => setSelectedTopLevel(e.target.value)}
                >
                  {topLevels.map(tl => (
                    <option key={tl} value={tl}>{tl}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired isDisabled={availableStates.length === 1 && availableStates[0] === 'ALL'}>
                <FormLabel>State</FormLabel>
                <Select 
                  placeholder="Select State" 
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                >
                  {availableStates.map(st => (
                    <option key={st} value={st}>{st === 'ALL' ? 'National (All States)' : st}</option>
                  ))}
                </Select>
              </FormControl>

              <Button 
                colorScheme="blue" 
                onClick={runResearch} 
                isLoading={loading}
                loadingText="Searching..."
                px={8}
                isDisabled={!selectedTopLevel || !selectedState}
              >
                Run Research
              </Button>
            </HStack>

            {currentEntity && (
              <Box mt={4} p={3} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
                <Text fontSize="sm" fontWeight="bold" color="blue.800">Target Entity: {currentEntity.entity}</Text>
                <Text fontSize="xs" color="blue.600">Domain: {currentEntity.domain}</Text>
                {currentEntity.notes && <Text fontSize="xs" color="gray.600" mt={1}>{currentEntity.notes}</Text>}
              </Box>
            )}
          </Box>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {loading && (
            <Box textAlign="center" py={10}>
              <Spinner size="xl" color="blue.500" mb={4} />
              <Text color="gray.600">Executing web search and extracting policy details...</Text>
              <Text fontSize="sm" color="gray.400">This may take 15-30 seconds.</Text>
            </Box>
          )}

          {results && (
            <Card shadow="md">
              <CardHeader bg="gray.50" borderBottomWidth="1px">
                <HStack justify="space-between">
                  <Heading size="md">Research Results</Heading>
                  <HStack>
                    {results.loaded_from_cache ? (
                      <Badge colorScheme="purple">Loaded from Database ⚡</Badge>
                    ) : (
                      <Badge colorScheme="orange">Live Web Search 🌐</Badge>
                    )}
                    {results.domain_restricted ? (
                      <Badge colorScheme="green">Domain Restricted: {results.target_domain}</Badge>
                    ) : (
                      <Badge colorScheme="yellow">No Domain Mapping Found</Badge>
                    )}
                    {results.search_successful ? (
                      <Badge colorScheme="blue">Search Successful</Badge>
                    ) : (
                      <Badge colorScheme="red">Search Failed</Badge>
                    )}
                  </HStack>
                </HStack>
              </CardHeader>
              
              <CardBody>
                <VStack align="stretch" spacing={6}>
                  
                  {results.sources_found && results.sources_found.length > 0 ? (
                    results.sources_found.map((source, index) => (
                      <Box key={index} borderWidth="1px" borderRadius="md" p={4} bg="white">
                        <HStack justify="space-between" mb={2}>
                          <Heading size="sm" color="blue.700">
                            {index + 1}. {source.title || 'Untitled Document'}
                          </Heading>
                          {source.url && (
                            <Link href={source.url} isExternal color="blue.500" fontSize="sm">
                              View Source <ExternalLinkIcon mx="2px" />
                            </Link>
                          )}
                        </HStack>
                        
                        <Divider my={3} />
                        
                        <Box>
                          <Text fontWeight="bold" color="gray.700" mb={2} fontSize="sm">LLM Targeted Extraction (Gemini)</Text>
                          <Box bg="gray.50" p={4} borderRadius="md" maxH="300px" overflowY="auto" borderWidth="1px">
                            <Text whiteSpace="pre-wrap" fontFamily="monospace" fontSize="sm" color="gray.800">
                              {source.raw_extracted_content || 'No content extracted.'}
                            </Text>
                          </Box>
                        </Box>

                        {source.full_page_text && (
                          <Box mt={4}>
                            <Accordion allowToggle>
                              <AccordionItem border="none">
                                <h2>
                                  <AccordionButton bg="blue.50" _hover={{ bg: "blue.100" }} borderRadius="md">
                                    <Box as="span" flex='1' textAlign='left' fontSize="sm" fontWeight="bold" color="blue.800">
                                      View Full Page Text (Tavily Extract)
                                    </Box>
                                    <AccordionIcon />
                                  </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4} px={0}>
                                  <Box bg="gray.100" p={4} borderRadius="md" maxH="400px" overflowY="auto" borderWidth="1px" mt={2}>
                                    <Text whiteSpace="pre-wrap" fontFamily="monospace" fontSize="xs" color="gray.700">
                                      {source.full_page_text}
                                    </Text>
                                  </Box>
                                </AccordionPanel>
                              </AccordionItem>
                            </Accordion>
                          </Box>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Alert status="warning">
                      <AlertIcon />
                      No specific policy documents were found or extracted.
                    </Alert>
                  )}

                </VStack>
              </CardBody>
            </Card>
          )}

        </VStack>
      </Box>
    </Box>
  );
};

export default PayerPolicyResearch;
